import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Employee } from '@app/models/employee/employee.model';
import { Payroll } from '@app/models/payroll/payroll.model';
import { BaseForm } from '@app/modules/forms/base.form';
import {
    CellUpdate,
    Column,
    ColumnMetadata,
    Metadata,
    PaystubTable,
    PaystubUpdateResponse,
    Row,
} from '@app/modules/payroll/interfaces/paystub-table.interface';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { FilterGroup } from '@app/modules/payroll/views/payroll/update/payroll-update.view';
import { AuthService, NotifyService } from '@app/services';
import { NumericString } from '@app/types/numeric-string.type';
import { TranslatableKey } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';
import {
    BodyScrollEvent,
    ColDef,
    ColGroupDef,
    ColumnVisibleEvent,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
} from 'ag-grid-community';
import { keyBy, keys, merge } from 'lodash-es';
import moment from 'moment';
import { CompensationTotalCellRenderer } from './cell-renderer/compensation/total.component';
import { EmployeeNameCellRenderer } from './cell-renderer/employee/name.component';

/**
 * Row type for v1 of the ag-grid implementation.
 * V2 uses the Row interface
 */
type AgGridDatum = {
    id: NumericString;
    employeeName: string;
    [key: string]: string | number;
};
type PaystubCell = {
    rowId: string;
    columnId: string;
    message?: string | TranslatableKey;
};

/* Global variables for AG-Grid callbacks that don't have access to the component instance */
let externalFilters: FilterGroup[] = [];
let payroll: Payroll;
let agGridCellMetadata: Metadata = {};

@Component({
    selector: 'app-form-payroll-edit-ag-grid',
    styleUrls: ['./edit-payroll.style.scss'],
    templateUrl: './edit-payroll.template.html',
})
export class EditPayrollForm extends BaseForm implements OnInit {
    @Input()
    public get payroll(): Payroll {
        return payroll;
    }
    public set payroll(value: Payroll) {
        payroll = value;
    }
    @Input()
    public get externalFilters(): FilterGroup[] {
        return externalFilters;
    }
    public set externalFilters(value: FilterGroup[]) {
        externalFilters = value;
    }
    @Input() showForm!: boolean;
    @Input() isParentLoading!: boolean;

    @Output() disableUserFromClickingNextStepEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    gridApi?: GridApi;
    paystubColumns: Column[] = [];
    paystubRows: Row[] = [];
    // TODO: This functionality is not yet implemented
    // fullscreen = false;
    incomeTypesVisibility: { visible: boolean; groupName: string; columnIds: string[] }[] = [];
    otherIncomeTypesVisibility: { visible: boolean; displayName?: string; columnId: string }[] = [];
    isLoading = false;

    private _columnDefs?: ColDef[];
    private _rowData?: AgGridDatum[];
    private compIdsWithRowspan = new Map<NumericString, number>();
    private autoSizeColumns: Set<string> = new Set([]);
    private get agGridCellMetadata(): Metadata {
        return agGridCellMetadata;
    }
    private set agGridCellMetadata(value: Metadata) {
        agGridCellMetadata = value;
    }
    private agGridReadOnly: PaystubCell[] = [];
    private paystubUpdates: PaystubCell[] = [];
    private paystubErrors: PaystubCell[] = [];
    private payByValues = { 'Direct Deposit': 'deposit', Cheque: 'cheque' };

    constructor(
        public auth: AuthService,
        private notify: NotifyService,
        private http: HttpClient,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.loadPaystubTable().then(() => {
            this.mapIncomeTypesForVisibilityControl();
            this.mapOtherIncomeTypesForVisibilityControl();
        });
    }

    /**
     * Set the visibility of income type columns (multiple columns within a group) in the grid.
     */
    setIncomeTypeVisibility(groupName: string, newVisibility: boolean): void {
        const entry = this.incomeTypesVisibility.find((incomeType) => incomeType.groupName === groupName);
        if (!entry) return;
        this.gridApi?.setColumnsVisible(entry.columnIds, newVisibility);
    }

    /**
     * Set the visibility of other income type columns in the grid.
     */
    setOtherIncomeTypeVisibility(columnId: string, newVisibility: boolean): void {
        this.gridApi?.setColumnsVisible([columnId], newVisibility);
    }

    /**
     * Update other income type column visibility when it's changed in the grid
     */
    onColumnVisible(event: ColumnVisibleEvent): void {
        // Disregard events that we triggered programmatically
        if (event.source === 'api') return;

        this.otherIncomeTypesVisibility = this.otherIncomeTypesVisibility.map((visibility) => {
            if (visibility.columnId === event.column?.getId()) {
                visibility.visible = event.visible ?? visibility.visible;
            }
            return visibility;
        });
    }

    /**
     * Update state of external filters
     *
     * This method is called by the parent component when the filters change
     */
    updateExternalFilters(): void {
        this.gridApi?.onFilterChanged();
    }

    /**
     * Let AG_Grid know if an external filter is active
     *
     * This method is passed to AG-Grid as a callback and therefore has no access to `this`
     */
    isExternalFilterPresent(this: undefined): boolean {
        return externalFilters.some(({ options }) => options.some(({ selected }) => selected));
    }

    /**
     * Whether a given row should be shown according to the external filters
     *
     * This method is passed to AG-Grid as a callback and therefore has no access to `this`
     */
    doesExternalFilterPass(this: undefined, node: IRowNode<AgGridDatum>): boolean {
        // If we return false from any of the external filters, the row will not be shown
        return externalFilters.every((group) => {
            if (node.id === undefined) return false;

            const values = group.options.filter((opt) => opt.selected).map((opt) => opt.value.toString());

            // If nothing selected for this filter, pass the row through
            if (!values.length) return true;

            const getHrEmployee = (): Employee | undefined => {
                if (node.id === undefined) return;
                return agGridCellMetadata[node.id]['employee.name'].hrEmployee;
            };

            switch (group.category) {
                case 'compensationType': {
                    const payBy = agGridCellMetadata[node.id]['paystub.pay_by'].name;
                    return values.includes(payBy);
                }

                case 'wageType': {
                    const compType = agGridCellMetadata[node.id]['compensation.type'].name;
                    return values.includes(compType);
                }

                case 'payments':
                    return values.some((value) => {
                        switch (value) {
                            case 'prorated':
                                return node.id && !!agGridCellMetadata[node.id]['regular_pay.total'].prorated;
                            case 'reimbursement':
                                return !!node.data?.['reimbursement'];
                            default:
                                return true;
                        }
                    });

                case 'additionalIncome':
                    // Find rows that have additional incomes matching the selected names
                    return Object.entries(agGridCellMetadata[node.id]).some(
                        ([colId, metadata]) =>
                            'name' in metadata && values.includes(metadata.name as string) && !!node.data?.[colId]
                    );

                case 'officeId': {
                    const hrEmployee = getHrEmployee();
                    return hrEmployee?.officeId && values.includes(hrEmployee.officeId.toString());
                }

                case 'departmentId': {
                    const hrEmployee = getHrEmployee();
                    return hrEmployee?.departmentId && values.includes(hrEmployee.departmentId.toString());
                }

                case 'status': {
                    const hrEmployee = getHrEmployee();
                    return values.some((value) => {
                        switch (value) {
                            // On leave: has a LOA overlapping with the pay period
                            case 'onLeave':
                                return !!hrEmployee?.leaveOfAbsences.some(
                                    (leaveOfAbsence) =>
                                        moment(leaveOfAbsence.start).isBetween(
                                            payroll.startDate,
                                            payroll.endDate,
                                            undefined,
                                            '[]'
                                        ) ||
                                        moment(leaveOfAbsence.end).isBetween(
                                            payroll.startDate,
                                            payroll.endDate,
                                            undefined,
                                            '[]'
                                        ) ||
                                        (moment(leaveOfAbsence.start).isBefore(payroll.startDate) &&
                                            moment(leaveOfAbsence.end).isAfter(payroll.endDate))
                                );

                            // New hire: started work within the pay period
                            case 'newHire':
                                return (
                                    hrEmployee?.hiredAt && new Date(hrEmployee.hiredAt) >= new Date(payroll.startDate)
                                );

                            // Rehired: returns to work within the pay period
                            case 'rehired':
                                return (
                                    hrEmployee?.nextDayOfWorkOn &&
                                    new Date(hrEmployee.nextDayOfWorkOn) >= new Date(payroll.startDate) &&
                                    new Date(hrEmployee.nextDayOfWorkOn) <= new Date(payroll.endDate)
                                );

                            // Terminated: stops working within the pay period and does not resume
                            case 'terminated':
                                return (
                                    hrEmployee?.lastDayOfWorkOn &&
                                    new Date(hrEmployee.lastDayOfWorkOn) <= new Date(payroll.endDate) &&
                                    (!hrEmployee.nextDayOfWorkOn ||
                                        new Date(hrEmployee.nextDayOfWorkOn) > new Date(payroll.endDate))
                                );
                        }
                    });
                }
            }
        });
    }

    async loadPaystubTable(): Promise<void> {
        try {
            this.isLoading = true;
            const paystubTable = await this.http.get<{ data: PaystubTable }>(this.getPaystubsPath()).toPromise();
            this.paystubColumns = paystubTable['data'].columns.filter(
                (column: Column) => column.column_id !== 'employee.name'
            );
            this.paystubRows = paystubTable['data'].rows;
        } catch (err) {
            this.notify.error(err || 'payroll.run.couldNotLoadSchedule');
        } finally {
            this.isLoading = false;
        }
    }

    getPaystubsPath(): string {
        return PayrollResources.PaystubsTable.replace(':payrollId', this.payroll.id.toString());
    }

    /**
     * Identify columns related to additional income types
     * and prepare them for toggling visibility
     */
    mapIncomeTypesForVisibilityControl(): void {
        // TODO might be better to do some of this on the backend
        this.incomeTypesVisibility = this.columnDefs
            .filter(
                (columnGroup) =>
                    !!columnGroup.headerName &&
                    'children' in columnGroup &&
                    (columnGroup as ColGroupDef).children.every((child) =>
                        (child as ColDef).field?.includes('CompanyAdditionalIncome')
                    )
            )
            .map((columnGroup) => ({
                groupName: columnGroup.headerName || '',
                columnIds: (columnGroup as ColGroupDef).children
                    .map((child) => (child as ColDef).field)
                    .filter(Boolean) as string[],
                visible: true,
            }));
    }

    /**
     * Identify columns related to other income types
     * and prepare them for toggling visibility
     */
    mapOtherIncomeTypesForVisibilityControl(): void {
        // TODO fetch from backend
        this.otherIncomeTypesVisibility = this.columnDefs
            .filter((columnGroup) => 'children' in columnGroup && !columnGroup.headerName)
            .flatMap((columnGroup) => (columnGroup as ColGroupDef).children)
            .filter((column) => (column as ColDef).field?.includes('CompanyAdditionalIncome'))
            .map((column) => ({
                columnId: (column as ColDef).field as string,
                displayName: column.headerName?.replace(' ($)', ''),
                visible: true,
            }));
    }

    /** Ag-grid rendering **/

    /* Column definitions */
    get columnDefs(): ColDef[] {
        if (this._columnDefs !== undefined) return this._columnDefs;

        const rowHeader: ColDef = {
            headerName: this.translate.instant('payroll.paystubs.tableColumns.employee'),
            field: 'employeeName',
            editable: false,
            pinned: 'left',
            filter: 'agTextColumnFilter',
            sort: 'asc',
            sortingOrder: ['asc', 'desc'],
            headerClass: 'parent-header',
            rowSpan: ({ data }) => {
                return this.calcRowSpan(data);
            },
            cellRenderer: EmployeeNameCellRenderer,
            cellRendererParams: {
                metadata: this.agGridCellMetadata,
            },
            cellClass: (): string => {
                return 'cell-employee';
            },
            cellClassRules: {
                'cell-employee-span': ({ column, node }) => {
                    return column.getRowSpan(node) > 1;
                },
            },
            comparator: this.customComparator.bind(this),
        };
        const columns: (ColDef<AgGridDatum> | ColGroupDef)[] = [];

        // eslint-disable-next-line camelcase
        this.paystubColumns.map(({ column_id, title, read_only, type, metadata }) => {
            const colDef = this.defaultColDef(column_id, title, type, read_only);

            if (metadata && metadata['parent']) {
                const column = columns.find((column) => column.headerName === metadata['parent']);
                if (column && 'children' in column) {
                    column['children'].push(colDef);
                } else {
                    columns.push({
                        headerName: metadata['parent'],
                        headerClass: 'parent-header',
                        children: [colDef],
                    });
                }
            } else {
                colDef['headerClass'] = 'parent-header';
                // add empty parent name to align text in botton
                columns.push({ headerName: '', children: [colDef] });
            }
        });
        this._columnDefs = [{ headerName: '', children: [rowHeader] }, ...columns];
        return this._columnDefs;
    }

    /* Row data */
    get rowData(): AgGridDatum[] {
        if (this._rowData !== undefined) return this._rowData;

        this._rowData = this.paystubRows.map((rowData: Row) => {
            const cells = rowData.cells;
            const nameCell = cells['employee.name'];

            if (typeof nameCell.value === 'string' || !nameCell.value.first_name) {
                throw Error(`Employee name column contains invalid data`);
            }
            const id = rowData.row_id;
            const fullNameInReverseOrder = `${nameCell.value.first_name} ${nameCell.value.last_name}`;

            const cellData = Object.entries(cells).reduce(
                (acc, [key, cell]) => {
                    if (key !== 'employee.name') {
                        acc[key] = cell.value;
                    }
                    if (cell.metadata) {
                        if (key === 'employee.name') Object.assign(cell.metadata, nameCell.value);

                        this.pushMetaData(rowData.row_id.toString(), { [key]: cell.metadata } as ColumnMetadata);
                    }
                    if (cell.read_only) {
                        this.agGridReadOnly.push({ rowId: rowData.row_id.toString(), columnId: key });
                    }
                    return acc;
                },
                {} as { [key: string]: any }
            );

            return {
                id,
                employeeName: fullNameInReverseOrder,
                ...cellData,
            };
        });

        this.fetchHrEmployeeData();

        return this._rowData;
    }

    /* Unique id of a row */
    getRowId(params: GetRowIdParams<AgGridDatum>): string {
        return params.data.id.toString();
    }

    onGridReady(params: GridReadyEvent): void {
        this.gridApi = params.api;
        // once sorting apply after page load, get the top rows
        this.calcRowspanOnFilteredAndSortData();
        params.api.redrawRows();
    }

    onBodyScroll(params: BodyScrollEvent): void {
        if (params.direction == 'horizontal') {
            const colIds: string[] = [];
            params.api.getAllDisplayedVirtualColumns().map((col) => {
                const columnName: string = col.getColId();
                if (!this.autoSizeColumns.has(columnName)) {
                    this.autoSizeColumns.add(columnName);
                    colIds.push(columnName);
                }
            });
            if (colIds.length > 0) {
                params.api.autoSizeColumns(colIds);
            }
        }
    }

    onSortChanged(event: any) {
        this.calcRowspanOnFilteredAndSortData();
        setTimeout(() => {
            event.api.redrawRows({ force: true });
        }, 0);
    }

    onFilterChanged(event: any): void {
        this.calcRowspanOnFilteredAndSortData();
        setTimeout(() => {
            event.api.redrawRows({ force: true });
        }, 0);
    }

    onFilterTextBoxChanged(query?: string) {
        this.gridApi?.setGridOption('quickFilterText', query);
    }

    private defaultColDef(
        fieldName: string,
        headerName: string,
        type: 'numeric' | 'text',
        readOnly: boolean = false
    ): ColDef<AgGridDatum> {
        return {
            field: fieldName,
            headerName: headerName,
            type: type === 'numeric' ? 'rightAligned' : [],
            cellDataType: false,
            sortable: false,

            cellEditorSelector: (params) => {
                if (fieldName === 'paystub.pay_by') {
                    return {
                        component: 'agRichSelectCellEditor',
                        params: {
                            values: keys(this.payByValues),
                        },
                    };
                }
            },
            editable: ({ data }): boolean => {
                if (this.agGridReadOnly.some(({ rowId, columnId }) => rowId === data?.id && columnId === fieldName)) {
                    return false;
                }

                return !readOnly;
            },
            rowSpan: ({ data }) => {
                if (fieldName == 'paystub.pay_by') {
                    return this.calcRowSpan(data);
                }
                return 1;
            },
            cellClass: ({ node, column }): string | string[] => {
                if (this.paystubErrors.some(({ rowId, columnId }) => rowId === node.id && columnId === fieldName)) {
                    return 'cell-error';
                }

                if (!column.isCellEditable(node)) return 'cell-readonly';

                const value = node.data && node.data[fieldName];

                if (!value && value !== 0) return 'cell-empty';

                return 'cell-filled';
            },
            cellClassRules: {
                'cell-span': ({ column, node }) => {
                    return column.getRowSpan(node) > 1;
                },
                'number-cell': ({ column, node }) => {
                    const colId = column.getColId();
                    return (
                        colId.startsWith('CompanyAdditionalIncome.') ||
                        colId.startsWith('regular_pay.') ||
                        colId.startsWith('overtime.') ||
                        colId == 'reimbursement'
                    );
                },
            },
            pinned: ['gross_pay', 'reimbursement'].includes(fieldName) ? 'right' : null,
            // Filter compensation info columns
            // TODO: add status here once we add the status column
            ...(['paystub.pay_by', 'compensation.name', 'compensation.type'].includes(fieldName)
                ? { filter: 'agSetColumnFilter' }
                : {}),
            cellRendererSelector: (params: ICellRendererParams) => {
                if (['regular_pay.total'].includes(fieldName)) {
                    return this.getCellRenderer(fieldName, params);
                }
            },
            onCellValueChanged: ({ node, column, oldValue, newValue }): void => {
                if (!node?.id) throw Error('No node or node id for cell update');
                if (oldValue === newValue) return;
                const columnName = column.getColId();
                if (columnName == 'paystub.pay_by') {
                    newValue = this.payByValues[newValue];
                }
                const update: CellUpdate = {
                    rowId: node.id,
                    columnId: columnName,
                    value: newValue,
                };
                const cell = this.paystubRows.find((row) => row.row_id.toString() === node.id)?.cells[columnName];
                if (cell) {
                    update.cellId = cell.cell_id?.toString();
                    update.updatedAt = cell.updated_at;
                }
                this.paystubUpdates.push({ rowId: node.id, columnId: columnName });
                this.onCellUpdate(update);
            },
            tooltipValueGetter: ({ node }): string | undefined => {
                if (!node || !node.id) return;

                // Fetch error message for the cell
                const error = this.paystubErrors.find(
                    ({ rowId, columnId }) => rowId === node.id && columnId === fieldName
                );
                if (error?.message) {
                    return this.translate.instant(error.message);
                }
            },
        };
    }

    get hasError(): boolean {
        return this.paystubErrors.length > 0;
    }

    get isSaving(): boolean {
        return this.paystubUpdates.length > 0;
    }

    private async onCellUpdate(update: CellUpdate): Promise<void> {
        try {
            this.disableUserFromClickingNextStepEmitter.emit(true);
            // Send the updates to the backend
            const { data } = await this.http
                .put<{ data: any[] }>(this.getPaystubUpdatePath(), { changes: [update] })
                .toPromise();

            this.updateCells(data);
        } catch (e) {
            this.highlightErrorAgGrid({ columnId: update.columnId as string, rowId: update.rowId });
            throw e;
        } finally {
            this.disableUserFromClickingNextStepEmitter.emit(false);
            this.paystubUpdates = this.paystubUpdates.filter(
                ({ rowId, columnId }) => !(rowId === update.rowId && columnId === update.columnId)
            );
        }
    }

    private updateCells(responses: PaystubUpdateResponse[]): void {
        for (const res of responses) {
            const rowId = res.row_id.toString();
            const columnId = res.column_id;
            if (res.status === 'ok') {
                this.paystubErrors = this.paystubErrors.filter(
                    (error) => !(error.rowId === rowId && error.columnId === columnId)
                );
                this.refreshRowData(res, rowId, columnId);
                this.refreshCells(rowId, columnId, res.value);
            } else {
                this.highlightErrorAgGrid({ rowId, columnId, message: res.message });
            }
        }
    }

    private refreshRowData(res: PaystubUpdateResponse, rowId: string, columnId: string) {
        const row = this.paystubRows.find((r) => r.row_id.toString() === rowId);
        if (row) {
            const cell = row?.cells[columnId];
            if (cell) {
                const { value, cell_id, updated_at } = res;
                Object.assign(cell, { value, cell_id, updated_at });
            }
        }
    }

    private refreshCells(rowId: string, columnId: string, value: string | number) {
        if (this._rowData) {
            let rowNode: IRowNode<any> | AgGridDatum | undefined = this._rowData.find((r) => r.id.toString() === rowId);
            if (rowNode) {
                rowNode[columnId] = value;
                rowNode = this.gridApi?.getRowNode(rowId);
                if (rowNode)
                    this.gridApi?.refreshCells({
                        rowNodes: [rowNode],
                        columns: [columnId],
                        force: true,
                    });
            }
        }
    }

    private getPaystubUpdatePath(): string {
        return PayrollResources.PaystubsTableUpdate.replace(':payrollId', this.payroll.id.toString());
    }

    private highlightErrorAgGrid(cell: PaystubCell): void {
        this.paystubErrors.push(cell);
        this.gridApi?.refreshCells({ force: true });
    }

    private calcRowSpan(data: AgGridDatum | undefined): number {
        if (this.agGridCellMetadata === undefined || data == undefined) {
            return 1;
        }
        if (this.compIdsWithRowspan.size == 0) this.calcRowspanOnFilteredAndSortData();
        return this.compIdsWithRowspan.get(data.id) || 1;
    }

    private pushMetaData(rowId: string, metadata: ColumnMetadata) {
        if (metadata) {
            this.agGridCellMetadata[rowId] = merge(this.agGridCellMetadata[rowId] || {}, metadata);
        }
    }

    private customComparator(valueA: string, valueB: string, nodeA: any, nodeB: any, isDescending: boolean) {
        // Retrieve node data for comparison
        const nodeAData: ColumnMetadata = this.agGridCellMetadata[nodeA.data.id];
        const nodeBData: ColumnMetadata = this.agGridCellMetadata[nodeB.data.id];
        // Compare last names (case insensitive)
        const lastName1 = nodeAData['employee.name'].last_name.toLowerCase();
        const lastName2 = nodeBData['employee.name'].last_name.toLowerCase();
        if (lastName1 < lastName2) return -1;
        if (lastName1 > lastName2) return 1;

        // Compare first names (case insensitive)
        const firstName1 = nodeAData['employee.name'].first_name.toLowerCase();
        const firstName2 = nodeBData['employee.name'].first_name.toLowerCase();
        if (firstName1 < firstName2) return -1;
        if (firstName1 > firstName2) return 1;

        // Compare compensation order for same employee
        const compOrder1 = nodeAData['employee.name'].compensation_number;
        const compOrder2 = nodeBData['employee.name'].compensation_number;
        const compOrder = compOrder1 < compOrder2 ? -1 : 1;

        return isDescending ? compOrder * -1 : compOrder;
    }

    private calcRowspanOnFilteredAndSortData(): void {
        const seenEmployeeIds = new Map<NumericString, NumericString>();
        // reset
        this.compIdsWithRowspan = new Map<NumericString, number>();
        if (this.gridApi) {
            // Get all the displayed rows in the grid
            this.gridApi.forEachNodeAfterFilterAndSort((node) => {
                const rowData = this.agGridCellMetadata[node.data.id];
                const hr_employee_id = rowData['employee.name'].hr_employee_id;
                // If this employee ID hasn't been seen yet, it's the first occurrence
                if (seenEmployeeIds.has(hr_employee_id)) {
                    const compId = seenEmployeeIds.get(hr_employee_id);
                    if (compId) {
                        const currentCount = this.compIdsWithRowspan.get(compId);
                        if (currentCount) this.compIdsWithRowspan.set(compId, currentCount + 1);
                    }
                } else {
                    seenEmployeeIds.set(hr_employee_id, node.data.id);
                    this.compIdsWithRowspan.set(node.data.id, 1);
                }
            });
        }
    }

    private getCellRenderer(fieldName: string, params: ICellRendererParams): any {
        if (!params.data) return;
        const metadata = this.agGridCellMetadata[params.data.id];
        switch (fieldName) {
            case 'regular_pay.total':
                return {
                    component: CompensationTotalCellRenderer,
                    params: {
                        values: {
                            prorated: metadata['regular_pay.total'].prorated,
                        },
                    },
                };
            default:
                break;
        }
    }

    /**
     * Augment the row metadata with HR employee data
     */
    private async fetchHrEmployeeData(): Promise<void> {
        const hrEmployeeIds = Object.values(this.agGridCellMetadata).map(
            (metadata) => metadata['employee.name'].hr_employee_id
        );

        const hrEmployees = await Employee.param('company', this.auth.company.id)
            .whereIn('id', hrEmployeeIds)
            .whereIn('status', ['active', 'terminated', 'onboarding', 'on leave'])
            .with('leaveOfAbsences')
            .all()
            .then(([employees]) => keyBy<Employee>(employees, 'id'));

        for (const id of Object.keys(this.agGridCellMetadata)) {
            const hrEmployeeId = this.agGridCellMetadata[id]['employee.name'].hr_employee_id;
            this.agGridCellMetadata[id]['employee.name'].hrEmployee = hrEmployees[hrEmployeeId];
        }
    }
}
